import type { Me as ContractMe, RoleAssignment, RoleKey } from '@myrewards/contracts';
import { LEVEL_ONE, type LevelOneId, type Me as ShellMe, type RoleGrant } from '../shell';

/** Plain role names, as people say them. Never the internal keys. */
export const ROLE_LABEL: Record<RoleKey, string> = {
  employee: 'Employee',
  manager: 'Manager',
  sector_lead: 'Sector Lead',
  delegate: 'Delegate',
  executive_delegate: 'Executive delegate',
  requester: 'Requester',
  executive: 'Executive',
  stock_admin: 'Stock Admin',
  payroll: 'Payroll',
  finance: 'Finance',
  system_admin: 'System Admin',
  paa_taa_approver: 'PAA/TAA approver',
  spot_reviewer: 'Spot Committee member',
  support: 'Support',
};

/** The order roles are listed in: the contract's RoleKey order (everyone, then elevated). */
const ROLE_ORDER: readonly RoleKey[] = Object.keys(ROLE_LABEL) as RoleKey[];

/**
 * Roles that earn a line under the name in the header. Employee is everyone; Manager is derived
 * from the directory and changes nothing about where a person works in the app.
 */
const NOT_ELEVATED: ReadonlySet<RoleKey> = new Set(['employee', 'manager']);

export function isElevated(role: RoleKey): boolean {
  return !NOT_ELEVATED.has(role);
}

/**
 * Roles another role already includes: an Executive can request any Executive Discretionary
 * Bonus, so being on the requesters list adds nothing to the line ("Executive", boards X2–X5).
 * "Your roles" still lists every grant.
 */
const INCLUDED_IN: Partial<Record<RoleKey, RoleKey>> = { requester: 'executive' };

function byRoleOrder(a: RoleAssignment, b: RoleAssignment): number {
  return ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role);
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values)];
}

/** "Maritime Airborne", "Maritime Airborne and Space Systems", "3 sectors". */
function sectorsLabel(assignments: readonly RoleAssignment[]): string | null {
  const names = unique(assignments.flatMap((a) => (a.sector ? [a.sector.name] : [])));
  if (names.length === 0) return null;
  if (names.length <= 2) return names.join(' and ');
  return `${names.length} sectors`;
}

/** "for Graham Whitlock", "for Graham Whitlock and Victor Osei". */
function forPeopleLabel(assignments: readonly RoleAssignment[]): string | null {
  const names = unique(assignments.flatMap((a) => (a.forPerson ? [a.forPerson.name] : [])));
  if (names.length === 0) return null;
  if (names.length <= 2) return `for ${names.join(' and ')}`;
  return `for ${names.length} Sector Leads`;
}

/**
 * Where a role applies, in the header's one line: a sector, or the person a delegate acts for. A
 * previewed delegate acts for nobody real (APP-059), so it shows its sector instead.
 */
function lineScope(role: RoleKey, assignments: readonly RoleAssignment[]): string | null {
  if (role === 'delegate') return forPeopleLabel(assignments) ?? sectorsLabel(assignments);
  return sectorsLabel(assignments);
}

/**
 * The line under the name in the header: elevated roles only, `null` for plain employees and
 * managers. One elevated role shows its scope ("Sector Lead · Maritime Airborne",
 * "Delegate · for Graham Whitlock"); several show their names ("Stock Admin · System Admin").
 */
export function roleLine(roles: readonly RoleAssignment[]): string | null {
  const held = new Set(roles.map((a) => a.role));
  const elevated = [...roles]
    .filter((a) => {
      const includer = INCLUDED_IN[a.role];
      return isElevated(a.role) && (includer === undefined || !held.has(includer));
    })
    .sort(byRoleOrder);
  const keys = unique(elevated.map((a) => a.role)) as RoleKey[];
  const [only] = keys;
  if (only === undefined) return null;
  if (keys.length === 1) {
    const scope = lineScope(only, elevated);
    return scope === null ? ROLE_LABEL[only] : `${ROLE_LABEL[only]} · ${scope}`;
  }
  const labels = keys.map((key) => ROLE_LABEL[key]);
  return labels.length <= 3
    ? labels.join(' · ')
    : `${labels.slice(0, 2).join(' · ')} · ${labels.length - 2} more`;
}

/** One row of "Your roles": the plain name and, where it matters, its scope. */
export function roleGrant(assignment: RoleAssignment): RoleGrant {
  const parts: string[] = [];
  if (assignment.role === 'delegate') {
    if (assignment.forPerson) parts.push(`for ${assignment.forPerson.name}`);
    else if (assignment.sector) parts.push(assignment.sector.name);
    if (assignment.needsApproval === true) parts.push('needs approval');
    if (assignment.needsApproval === false) parts.push('no approval needed');
  } else if (assignment.sector) {
    parts.push(assignment.sector.name);
  }
  const grant: RoleGrant = {
    role: ROLE_LABEL[assignment.role],
    tone: isElevated(assignment.role) ? 'info' : 'neutral',
  };
  return parts.length > 0 ? { ...grant, scope: parts.join(' · ') } : grant;
}

/**
 * Every role for "Your roles" in the account menu, in role order. Everyone is an Employee, so
 * Employee is always listed even if the server leaves it implicit.
 */
export function yourRoles(roles: readonly RoleAssignment[]): RoleGrant[] {
  const sorted = [...roles].sort(byRoleOrder);
  const grants = sorted.map(roleGrant);
  if (!sorted.some((a) => a.role === 'employee'))
    grants.unshift({ role: 'Employee', tone: 'neutral' });
  // The same role and scope twice (two identical grants) shows once.
  const seen = new Set<string>();
  return grants.filter((g) => {
    const key = `${g.role}|${g.scope ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Role names in Preview as. The panel's role cards, the banner and the audit log (the server's
 * `previewLabel`) all say them this way.
 */
export const PREVIEW_ROLE_LABEL: Record<RoleKey, string> = {
  ...ROLE_LABEL,
};

/** Roles that work in one sector; a preview of them names it. */
const SCOPED_ROLES: ReadonlySet<RoleKey> = new Set(['sector_lead', 'delegate']);

/**
 * "Employee + Sector Lead (Maritime Airborne)": the role mix a Preview session shows, Employee
 * first and in role order, each sector-scoped role with its sector.
 */
export function previewLabel(
  roles: readonly RoleKey[],
  sector: { name: string } | null = null,
): string {
  const held = new Set<RoleKey>(['employee', ...roles]);
  return ROLE_ORDER.filter((key) => held.has(key))
    .map((key) =>
      sector !== null && SCOPED_ROLES.has(key)
        ? `${PREVIEW_ROLE_LABEL[key]} (${sector.name})`
        : PREVIEW_ROLE_LABEL[key],
    )
    .join(' + ');
}

/**
 * APP-232: the roles as the header, Your roles and the Preview banner name them: the person's
 * grants, with the Spot Committee seat as the server says it is today (`spotCommittee`, from the
 * rule every Spot page and Preview as' Spot Committee card use), not as a grant says it was. So
 * someone named in Spot Award setup reads "Stock Admin · Spot Committee member", and a grant left
 * from before the committee was named doesn't. Without the field (a sample preview, whose roles
 * are the previewed combination, or an older server), the roles as they are. Only what's shown:
 * no grant is made, and nothing else reads this.
 */
export function shownRoles(me: Pick<ContractMe, 'roles' | 'spotCommittee'>): RoleAssignment[] {
  if (me.spotCommittee === undefined) return [...me.roles];
  const others = me.roles.filter((a) => a.role !== 'spot_reviewer');
  if (!me.spotCommittee) return others;
  return [
    ...others,
    { role: 'spot_reviewer', sector: null, forPerson: null, needsApproval: null, awardTypes: null },
  ];
}

/** The signed-in person as the shell shows them. */
export function presentMe(me: ContractMe): ShellMe {
  const roles = shownRoles(me);
  const line = roleLine(roles);
  return {
    name: me.person.displayName,
    // SET1: shown next to the name on the avatar menu's profile card, nowhere else.
    ...(me.person.pronouns != null && { pronouns: me.person.pronouns }),
    initials: me.person.initials,
    roleLine: line,
    roles: yourRoles(roles),
    avatarTone: line === null ? 'gold' : 'petrol',
  };
}

/** The Level 1 items, exactly as the server decided them (the shell keeps the fixed order). */
export function levelOne(me: ContractMe): LevelOneId[] {
  return [...me.nav];
}

/** True when the person holds this role (in any scope). */
export function hasRole(me: Pick<ContractMe, 'roles'>, role: RoleKey): boolean {
  return me.roles.some((a) => a.role === role);
}

/** Every role mix opens its personal/blended Home; operational queues have their own links. */
export const homeHref: (me: { roles: readonly RoleAssignment[] }) => string = () => '/';

/**
 * Where sign-in lands (SET1, APP-081): the person's start page, already checked against their
 * Level 1 on the server. Home is `/`, which opens their own Home.
 */
export function startHref(me: Pick<ContractMe, 'preferences'>): string {
  const page = me.preferences.startPage ?? 'home';
  if (page === 'home') return '/';
  if (page === 'admin') return '/settings/system';
  return LEVEL_ONE.find((item) => item.id === page)?.href ?? '/';
}
